import { getClientId } from '@/utils/helper';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { RootState } from './store';

// Loaded inside the thunks, not at module scope: api/axios imports the
// store, and this slice is reached before the store finishes
// initialising, which put its reducer in the temporal dead zone.
const axiosInstance = async () => (await import('@/api/axios')).default;

export interface PermissionDefinition {
  key: string;
  label: string;
  description?: string;
  /** Semantic hierarchy: "Edit leads" implies "View leads". */
  implies?: string[];
  /**
   * APIs the page behind this permission also calls. Kept separate from
   * `implies` so the builder can word the lock reason correctly.
   */
  requires?: string[];
  platformOnly?: boolean;
}

export interface PermissionModule {
  module: string;
  label: string;
  description: string;
  group: string;
  permissions: PermissionDefinition[];
}

/** An extra capability offered once a surface is switched on. */
export interface SurfaceAddOn {
  key: string;
  label: string;
  description?: string;
}

/**
 * One screen the admin can open. The server has already filtered these to what
 * this actor may hand out, so anything present is safe to offer.
 */
export interface UiSurface {
  id: string;
  label: string;
  /** How the admin gets to it, shown under the row. */
  where: string;
  /** Route relative to the client segment. Absent on a toggle. */
  path?: string;
  description: string;
  section: string;
  kind: 'page' | 'toggle';
  view: string[];
  /** Absent when this actor cannot grant edit rights on the screen. */
  edit?: string[];
  addOns?: SurfaceAddOn[];
}

interface CataloguePayload {
  surfaces: UiSurface[];
  catalogue: PermissionModule[];
  isPlatform: boolean;
}

export interface Role {
  _id: string;
  organization_id: string | null;
  name: string;
  slug: string;
  description?: string;
  permissions: string[];
  /**
   * Screen ids this role reaches. `[]` means none, deliberately; `null` means
   * the role predates ticks and the builder should match on keys instead.
   */
  surfaces?: string[] | null;
  status: 'active' | 'inactive';
  isSystem: boolean;
  systemKey: string | null;
  scopeLevel: 'organization' | 'region' | 'branch';
  memberCount: number;
  editable: boolean;
  createdAt: string;
  updatedAt: string;
  /** Populated by the API. Null on seeded system roles. */
  createdBy?: RoleAuditUser | null;
  updatedBy?: RoleAuditUser | null;
}

export interface RoleAuditUser {
  _id: string;
  name?: string;
  email?: string;
}

interface RolesState {
  roles: Role[];
  /** What the builder renders. */
  surfaces: UiSurface[];
  /** Backs the builder's dependency closure and the advanced list. */
  catalogue: PermissionModule[];
  /** True for Keepme staff, who also get the raw catalogue. */
  isPlatform: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: RolesState = {
  roles: [],
  surfaces: [],
  catalogue: [],
  isPlatform: false,
  loading: false,
  saving: false,
  error: null,
};

const message = (error: any, fallback: string): string =>
  error?.response?.data?.message ?? error?.message ?? fallback;

export const fetchRoles = createAsyncThunk(
  'roles/fetchAll',
  async (includeArchived: boolean | undefined, { rejectWithValue }) => {
    try {
      // Scope to the client being viewed. A Super Owner has no organisation
      // of their own, so without this the API cannot tell which client's
      // roles are wanted and returns every client's.
      const res = await (await axiosInstance()).get('/roles', {
        params: {
          ...(includeArchived ? { includeArchived: 'true' } : {}),
          ...(getClientId() ? { client: getClientId() } : {}),
        },
      });
      return res.data.data as Role[];
    } catch (error: any) {
      return rejectWithValue(message(error, 'Failed to fetch roles'));
    }
  }
);

export const fetchCatalogue = createAsyncThunk(
  'roles/fetchCatalogue',
  async (_, { rejectWithValue }) => {
    try {
      const res = await (await axiosInstance()).get('/roles/permission-catalogue');
      const data = res.data.data;
      // Tolerates the old bare-array shape during a rolling deploy.
      return Array.isArray(data)
        ? { surfaces: [], catalogue: data as PermissionModule[], isPlatform: false }
        : (data as CataloguePayload);
    } catch (error: any) {
      return rejectWithValue(message(error, 'Failed to fetch permissions'));
    }
  }
);

export const createRole = createAsyncThunk(
  'roles/create',
  async (
    payload: {
      name: string;
      description?: string;
      permissions: string[];
      surfaces?: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await (await axiosInstance()).post('/roles', payload, {
        params: getClientId() ? { client: getClientId() } : undefined,
      });
      return res.data.data as Role;
    } catch (error: any) {
      return rejectWithValue(message(error, 'Failed to create role'));
    }
  }
);

export const updateRole = createAsyncThunk(
  'roles/update',
  async (
    payload: {
      id: string;
      name?: string;
      description?: string;
      permissions?: string[];
      surfaces?: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const { id, ...body } = payload;
      const res = await (await axiosInstance()).patch(`/roles/${id}`, body);
      return res.data.data as Role;
    } catch (error: any) {
      return rejectWithValue(message(error, 'Failed to update role'));
    }
  }
);

export const cloneRole = createAsyncThunk(
  'roles/clone',
  async (
    payload: { id: string; name: string; description?: string },
    { rejectWithValue }
  ) => {
    try {
      const { id, ...body } = payload;
      const res = await (await axiosInstance()).post(`/roles/${id}/clone`, body, {
        params: getClientId() ? { client: getClientId() } : undefined,
      });
      return res.data.data as Role;
    } catch (error: any) {
      return rejectWithValue(message(error, 'Failed to clone role'));
    }
  }
);

export const setRoleStatus = createAsyncThunk(
  'roles/setStatus',
  async (
    payload: { id: string; status: 'active' | 'inactive' },
    { rejectWithValue }
  ) => {
    try {
      const res = await (await axiosInstance()).patch(`/roles/${payload.id}/status`, {
        status: payload.status,
      });
      return res.data.data as Role;
    } catch (error: any) {
      return rejectWithValue(message(error, 'Failed to change role status'));
    }
  }
);

export const deleteRole = createAsyncThunk(
  'roles/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await (await axiosInstance()).delete(`/roles/${id}`);
      return id;
    } catch (error: any) {
      // 409 carries the "still assigned" explanation — surface it verbatim.
      return rejectWithValue(message(error, 'Failed to delete role'));
    }
  }
);

export const assignRole = createAsyncThunk(
  'roles/assign',
  async (payload: { userId: string; roleId: string }, { rejectWithValue }) => {
    try {
      const res = await (await axiosInstance()).patch(`/roles/assign/${payload.userId}`, {
        roleId: payload.roleId,
      });
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(message(error, 'Failed to assign role'));
    }
  }
);

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearRolesError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoles.fulfilled, (state, action) => {
        state.roles = action.payload;
        state.loading = false;
      })
      .addCase(fetchRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCatalogue.fulfilled, (state, action) => {
        state.surfaces = action.payload.surfaces ?? [];
        state.catalogue = action.payload.catalogue ?? [];
        state.isPlatform = !!action.payload.isPlatform;
      })
      .addCase(deleteRole.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        // Handled here rather than in the loop below — RTK rejects two
        // reducers for the same action type.
        state.roles = state.roles.filter((r) => r._id !== action.payload);
        state.saving = false;
      });

    // Every other mutation shares the same saving/error handling.
    for (const thunk of [createRole, updateRole, cloneRole, setRoleStatus, assignRole]) {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.saving = false;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.saving = false;
          state.error = action.payload as string;
        });
    }
  },
});

export const { clearRolesError } = rolesSlice.actions;
export const selectRoles = (state: RootState) => state.roles;
export default rolesSlice.reducer;
